import type { SelectedNetworkPath } from '../streamRecovery/contracts';
import type { SelectedPathReader } from '../streamRecovery/interfaces';

/** Reads the selected video transport, never an unrelated succeeded ICE pair. */
export class RtcSelectedPathReader implements SelectedPathReader {
  read(report: RTCStatsReport, transportId?: string): SelectedNetworkPath {
    const entries = new Map<string, Record<string, unknown>>();
    const videoTransports = new Set<string>();
    const legacySelected: Record<string, unknown>[] = [];
    report.forEach((stat) => {
      const entry = stat as Record<string, unknown>;
      if (typeof entry['id'] === 'string') entries.set(entry['id'], entry);
      if ((entry['type'] === 'inbound-rtp' || entry['type'] === 'outbound-rtp') &&
          (entry['kind'] === 'video' || entry['mediaType'] === 'video') && typeof entry['transportId'] === 'string') {
        videoTransports.add(entry['transportId']);
      }
      if (entry['type'] === 'candidate-pair' && entry['selected'] === true) legacySelected.push(entry);
    });
    const selectedTransport = transportId ?? (videoTransports.size === 1 ? [...videoTransports][0] : undefined);
    const transport = selectedTransport ? entries.get(selectedTransport) : undefined;
    const pairId = transport?.['selectedCandidatePairId'];
    const pair = typeof pairId === 'string' ? entries.get(pairId) : undefined;
    if (pair?.['type'] === 'candidate-pair') return this.result(pair, entries, selectedTransport);
    if (legacySelected.length === 1) return this.result(legacySelected[0], entries);
    return { selection: 'unavailable' };
  }

  private result(pair: Record<string, unknown>, entries: Map<string, Record<string, unknown>>, transportId?: string): SelectedNetworkPath {
    if (typeof pair['id'] !== 'string' || !pair['id']) return { selection: 'unavailable' };
    const rtt = pair['currentRoundTripTime'];
    const candidates = [pair['localCandidateId'], pair['remoteCandidateId']]
      .map((id) => typeof id === 'string' ? entries.get(id) : undefined);
    const candidateTypes = candidates.map((candidate) => candidate?.['candidateType']);
    return {
      selection: 'selected', selectedPairId: pair['id'],
      ...(transportId ? { transportId } : {}),
      ...(typeof rtt === 'number' && Number.isFinite(rtt) && rtt >= 0 ? { rttMs: rtt * 1000 } : {}),
      ...(candidateTypes.includes('relay') ? { relay: true } :
        candidateTypes.every((type) => typeof type === 'string') ? { relay: false } : {}),
    };
  }
}

